import React from 'react';
import withStyles from '@material-ui/styles/withStyles';
import { Typography, Box } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import {
  EMPTY_STRING,
  MAX_AUDIT_TRAIL_UPDATE_STRING_SIZE,
} from 'app/app.constants';

const HtmlTooltip = ({ className, ...props }) => (
  <Tooltip {...props} classes={className} placement="right-start" />
);

const RiskScoreCard = ({ score, date, classes, risk, prevRiskScore }) => {
  const normalizeTitle = (t) => (t || EMPTY_STRING).trim().toLowerCase();

  const newList = (risk.newPickListOptions || []).map((newItem) => {
    const oldList = risk.oldPickListOptions || [];

    const existedBefore = oldList.some(
      (old) =>
        normalizeTitle(old.title) === normalizeTitle(newItem.title) &&
        Number(old.score) === Number(newItem.score),
    );

    const updated = !existedBefore;

    return {
      ...newItem,
      updated,
    };
  });

  const optionsChanged = newList.some((item) => item.updated);

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.leftColumn}>
          <Typography className={classes.label}>Change</Typography>
          <Typography className={classes.valuePrimary}>
            {risk.integrityTitle ?? 'None'}
          </Typography>

          <Box mt={2}>
            <Typography className={classes.label}>Old Value:</Typography>
            <Typography className={classes.valuePrimary}>
              {risk.oldTitle ?? 'None'}
              {risk.oldTitle === EMPTY_STRING && 'None'}
              {(risk.oldPickListOptions?.length > 0 && optionsChanged) ||
              (Number(score) === Number(prevRiskScore) &&
                risk.newTitle === EMPTY_STRING) ? (
                <>
                  {' - '}
                  <HtmlTooltip
                    title={
                      <>
                        <Box className={classes.toolTipContainer}>
                          <Box>
                            {risk.oldPickListOptions != null &&
                              risk.oldPickListOptions.map((r) => {
                                return (
                                  <Typography>
                                    {(r.title || EMPTY_STRING).length >
                                    MAX_AUDIT_TRAIL_UPDATE_STRING_SIZE
                                      ? `${(r.title || EMPTY_STRING).slice(
                                          0,
                                          MAX_AUDIT_TRAIL_UPDATE_STRING_SIZE,
                                        )}...`
                                      : r.title}
                                    : {r.score}
                                  </Typography>
                                );
                              })}
                          </Box>
                        </Box>
                      </>
                    }
                  >
                    <Chip
                      label={`+${risk.oldPickListOptions?.length || 0}`}
                      className={classes.toolTipChip}
                    />
                  </HtmlTooltip>
                </>
              ) : null}
            </Typography>
          </Box>

          <Box mt={2}>
            <Typography className={classes.label}>New Value:</Typography>
            <Typography className={classes.valuePrimary}>
              {risk.newTitle ?? 'None'}
              {risk.newTitle === EMPTY_STRING && 'None'}

              {(risk.newPickListOptions?.length > 0 && optionsChanged) ||
              (Number(score) === Number(prevRiskScore) &&
                risk.newTitle === EMPTY_STRING) ? (
                <>
                  {' - '}
                  <HtmlTooltip
                    title={
                      <>
                        <Box className={classes.toolTipContainer}>
                          <Box>
                            {newList.map((r) => (
                              <Typography key={r.title}>
                                {(r.title || EMPTY_STRING).length >
                                MAX_AUDIT_TRAIL_UPDATE_STRING_SIZE
                                  ? `${(r.title || EMPTY_STRING).slice(
                                      0,
                                      MAX_AUDIT_TRAIL_UPDATE_STRING_SIZE,
                                    )}...`
                                  : r.title}
                                : {r.score}
                                {r.updated && (
                                  <Chip
                                    label="updated"
                                    color="primary"
                                    size="small"
                                    className={classes.toolTipChip}
                                  />
                                )}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      </>
                    }
                  >
                    <Chip
                      label={`+${risk.newPickListOptions?.length || 0}`}
                      className={classes.toolTipChip}
                    />
                  </HtmlTooltip>
                </>
              ) : null}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.divider} />

        <Box className={classes.rightColumn}>
          <Typography className={classes.label}>Date &amp; Time</Typography>
          <Typography className={classes.valuePrimary}>{date}</Typography>

          <Box mt={2}>
            <Typography className={classes.label}>Risk Score</Typography>
            <Typography className={classes.valuePrimary}>{score}</Typography>
          </Box>

          <Box mt={2}>
            <Typography className={classes.label}>Done by</Typography>
            <Typography className={classes.valuePrimary}>
              {risk.updatedBy ?? 'None'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.secondary.darkGrey,
    borderRadius: theme.spacing(0.75),
    padding: theme.spacing(2),
    color: theme.palette.common.white,
    width: '100%',
    boxSizing: 'border-box',
    margin: theme.spacing(1),
  },
  toolTipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
  },
  toolTipChip: {
    marginLeft: theme.spacing(1),
  },
  leftColumn: {
    flex: 2,
    paddingRight: theme.spacing(2),
  },
  rightColumn: {
    flex: 1,
    paddingLeft: theme.spacing(2),
  },
  divider: {
    borderLeft: `1px solid ${theme.palette.grey[700]}`,
    margin: theme.spacing(0, 1),
  },
  label: {
    fontSize: theme.spacing(1.5),
    letterSpacing: 0.5,
    color: theme.palette.grey[500],
  },
  valuePrimary: {
    fontSize: theme.spacing(2),
    fontWeight: 500,
    marginTop: theme.spacing(0.3),
  },
  valueSecondary: {
    fontSize: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
});

export default withStyles(styles)(RiskScoreCard);
